// [PURPOSE]: Account management panel in Settings tab
// [WHY]: Users need to view profile, premium status, AI tokens, and manage billing
// [INTEGRATION]: Rendered in Settings tab when user is authenticated
// [CREATED]: v1.0 Track 2A — Account management UI

import React, { useState, useMemo, lazy, Suspense, useCallback } from 'react';
import { User, Crown, Sparkles, LogOut, RefreshCw, CreditCard, Image, Trash2, Link2, Link2Off, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { useCharacter } from '../contexts/CharacterContext';
import { useSettings } from '../contexts/SettingsContext';
import { useConfirm } from '../contexts/ConfirmContext';
import AuthModal from './AuthModal';
import { initiatePremiumPurchase, initiateTokenPurchase } from '../utils/purchaseUtils';
import { reportError } from '../services/errorReporter';

const ImageGallery = lazy(() => import('./ImageGallery'));

/**
 * [PURPOSE]: Account panel showing profile, premium status, and token balance
 * [UX]: Integrated into Settings tab, shows different states for free/premium/unauthenticated
 */
const AccountPanel = () => {
  const { currentUser, isAuthenticated, logout, loading, linkGoogleAccount, unlinkGoogleAccount } = useAuth();
  const { isPremium, aiTokens, premiumStatus, refreshPremiumStatus } = useSubscription();
  const { playerStats: character, setPlayerStats: setCharacter } = useCharacter();
  const { savedCharacters } = useSettings();
  const confirm = useConfirm();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  // [P2.15]: Google linking UI state
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkMessage, setLinkMessage] = useState(null); // { type: 'success'|'error', text: string }

  // [P2.15]: Detect linked providers from Firebase Auth user record.
  // currentUser.providerData is an array of { providerId, ... } entries.
  const linkedProviderIds = useMemo(
    () => (currentUser?.providerData || []).map(p => p?.providerId).filter(Boolean),
    [currentUser]
  );
  const isGoogleLinked = linkedProviderIds.includes('google.com');

  // [v1.1]: Aggregate images from ALL characters (account-wide gallery)
  const allGalleryImages = useMemo(() => {
    const images = [];
    const currentName = character?.name || 'Current Character';

    // Current character's gallery (may not be saved yet)
    (character?.imageGallery || []).forEach(img => {
      images.push({ ...img, characterName: currentName, isCurrent: true });
    });

    // All saved characters' galleries (skip current to avoid duplicates)
    (savedCharacters || []).forEach(sc => {
      const charData = sc?.data?.playerStats || sc;
      const gallery = charData?.imageGallery;
      if (!gallery?.length) return;
      const charName = charData?.name || sc?.name || 'Unknown';
      if (charName === currentName) return; // Skip current character (already added)
      gallery.forEach(img => {
        images.push({ ...img, characterName: charName });
      });
    });

    return images;
  }, [character, savedCharacters]);

  const galleryCount = allGalleryImages.length;
  const portraitCount = allGalleryImages.filter(i => i.type === 'portrait').length;
  const iconCount = allGalleryImages.filter(i => i.type === 'icon').length;
  const characterCount = new Set(allGalleryImages.map(i => i.characterName)).size;

  // [NOTE]: Delete only works for current character's images (can't mutate savedCharacters from here)
  const deleteFromGallery = useCallback((imageId) => {
    setCharacter(prev => ({
      ...prev,
      imageGallery: (prev.imageGallery || []).filter(img => img.id !== imageId),
    }));
  }, [setCharacter]);
  const [refreshing, setRefreshing] = useState(false);

  // ========================================
  // HANDLERS
  // ========================================

  const handleLogout = async () => {
    await logout();
  };

  const handleRefreshStatus = async () => {
    setRefreshing(true);
    await refreshPremiumStatus();
    setTimeout(() => setRefreshing(false), 1000);
  };

  // [P2.15]: Trigger Google account linking via Firebase popup.
  const handleLinkGoogle = async () => {
    setLinkBusy(true);
    setLinkMessage(null);
    try {
      const result = await linkGoogleAccount();
      if (result?.success) {
        setLinkMessage({ type: 'success', text: 'Google account linked' });
      } else if (result?.error) {
        setLinkMessage({ type: 'error', text: result.error });
      }
    } finally {
      setLinkBusy(false);
    }
  };

  // [P2.15]: Trigger Google account unlinking with a confirm prompt.
  // [UI-CONFIRM-UNIFICATION 2026-07-04]: styled async confirm replaces window.confirm.
  const handleUnlinkGoogle = async () => {
    const ok = await confirm({
      title: 'Unlink Google Account',
      message: 'Unlink your Google account? You can re-link it at any time.',
      confirmText: 'Unlink',
      confirmColor: 'red',
    });
    if (!ok) return;
    setLinkBusy(true);
    setLinkMessage(null);
    try {
      const result = await unlinkGoogleAccount();
      if (result?.success) {
        setLinkMessage({ type: 'success', text: 'Google account unlinked' });
      } else if (result?.error) {
        setLinkMessage({ type: 'error', text: result.error });
      }
    } finally {
      setLinkBusy(false);
    }
  };

  // ========================================
  // UNAUTHENTICATED STATE
  // ========================================

  if (!isAuthenticated) {
    return (
      <>
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 p-5">
          <div className="text-center">
            <User size={40} className="text-gray-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-white mb-2">Sign In</h3>
            <p className="text-sm text-gray-400 mb-4">
              Create an account to unlock premium features, sync across devices, and generate AI art.
            </p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors"
            >
              Sign In / Sign Up
            </button>
          </div>
        </div>

        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      </>
    );
  }

  // ========================================
  // AUTHENTICATED STATE
  // ========================================

  return (
    <div className="space-y-4">
      {/* Profile Section */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
              {currentUser?.displayName?.[0]?.toUpperCase() || currentUser?.email?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <h3 className="font-bold text-white">
                {currentUser?.displayName || 'Adventurer'}
              </h3>
              <p className="text-xs text-gray-400">{currentUser?.email}</p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="px-3 py-2 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg flex items-center gap-1 transition-colors"
          >
            <LogOut size={14} />
            Sign Out
          </button>
        </div>

        {/* Premium Badge */}
        <div className={`p-3 rounded-lg border ${isPremium
          ? 'bg-amber-900/20 border-amber-600/30'
          : 'bg-gray-800 border-gray-600/30'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isPremium ? (
                <Crown size={18} className="text-amber-400" />
              ) : (
                <Crown size={18} className="text-gray-500" />
              )}
              <span className={`font-medium ${isPremium ? 'text-amber-300' : 'text-gray-400'}`}>
                {isPremium ? 'Premium' : 'Free Tier'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleRefreshStatus}
                disabled={refreshing}
                className="p-1 text-gray-400 hover:text-white transition-colors"
                title="Refresh premium status"
              >
                <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              </button>

              {!isPremium && (
                <button
                  onClick={async () => {
                    try {
                      await initiatePremiumPurchase(currentUser);
                    } catch (err) {
                      reportError(err, { area: 'account_premium_purchase' });
                    }
                  }}
                  className="px-3 py-1 text-xs bg-amber-600 hover:bg-amber-500 text-white rounded font-medium transition-colors"
                >
                  Upgrade $3.99
                </button>
              )}
            </div>
          </div>

          {isPremium && premiumStatus.purchaseDate && (
            <p className="text-xs text-gray-400 mt-1">
              Premium since {new Date(premiumStatus.purchaseDate).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* [P2.15]: Google Account Linking */}
        <div className="mt-3 p-3 rounded-lg border bg-gray-800 border-gray-600/30">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {isGoogleLinked ? (
                <CheckCircle size={16} className="text-green-400 flex-shrink-0" />
              ) : (
                <Link2 size={16} className="text-gray-400 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <div className={`text-sm font-medium ${isGoogleLinked ? 'text-green-300' : 'text-gray-300'}`}>
                  {isGoogleLinked ? 'Google Account Linked' : 'Link Google Account'}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {isGoogleLinked
                    ? 'You can sign in with Google or your email/password.'
                    : 'Add Google sign-in to your existing account.'}
                </div>
              </div>
            </div>

            {isGoogleLinked ? (
              <button
                type="button"
                onClick={handleUnlinkGoogle}
                disabled={linkBusy}
                className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-gray-200 rounded font-medium transition-colors flex items-center gap-1 flex-shrink-0"
              >
                <Link2Off size={12} />
                Unlink
              </button>
            ) : (
              <button
                type="button"
                onClick={handleLinkGoogle}
                disabled={linkBusy}
                className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded font-medium transition-colors flex items-center gap-1 flex-shrink-0"
              >
                <Link2 size={12} />
                Link Google Account
              </button>
            )}
          </div>

          {linkMessage && (
            <p className={`mt-2 text-xs ${linkMessage.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>
              {linkMessage.text}
            </p>
          )}
        </div>
      </div>

      {/* AI Tokens Section */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-bold text-white flex items-center gap-2">
            <Sparkles size={18} className="text-purple-400" />
            AI Generation Tokens
          </h4>
          <span className="text-2xl font-bold text-purple-300">{aiTokens}</span>
        </div>

        <p className="text-xs text-gray-400 mb-3">
          Generate custom item icons (1 token) and character portraits (3 tokens) using AI.
        </p>

        {/* Token cost reference */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="p-2 bg-gray-800 rounded border border-gray-700 text-center">
            <Image size={16} className="text-blue-400 mx-auto mb-1" />
            <div className="text-xs text-gray-300 font-medium">Item Icon</div>
            <div className="text-xs text-gray-500">1 token</div>
          </div>
          <div className="p-2 bg-gray-800 rounded border border-gray-700 text-center">
            <User size={16} className="text-green-400 mx-auto mb-1" />
            <div className="text-xs text-gray-300 font-medium">Portrait</div>
            <div className="text-xs text-gray-500">3 tokens</div>
          </div>
        </div>

        {/* Token packs */}
        {isPremium && (
          <div className="space-y-1">
            <p className="text-xs text-gray-500 font-medium mb-2">Buy More Tokens:</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { name: 'Standard', tokens: 150, price: '$4.99', packId: 'standard' },
                { name: 'Bulk', tokens: 350, price: '$9.99', packId: 'bulk' },
                { name: 'Ultimate', tokens: 800, price: '$19.99', packId: 'ultimate' },
              ].map(pack => (
                <button
                  key={pack.name}
                  onClick={async () => {
                    try {
                      await initiateTokenPurchase(currentUser, pack.packId);
                    } catch (err) {
                      reportError(err, { area: 'account_token_purchase' });
                    }
                  }}
                  className="p-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded-lg text-center transition-colors"
                >
                  <div className="text-xs font-medium text-purple-300">{pack.tokens} tokens</div>
                  <div className="text-xs text-gray-400">{pack.price}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {!isPremium && (
          <div className="p-2 bg-gray-800/50 rounded border border-gray-700 text-center">
            <p className="text-xs text-gray-400">
              Upgrade to Premium to get 100 free tokens and access AI generation.
            </p>
          </div>
        )}

        {/* Stats */}
        {premiumStatus.totalImagesGenerated > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-700">
            <p className="text-xs text-gray-500">
              Total images generated: {premiumStatus.totalImagesGenerated}
            </p>
          </div>
        )}
      </div>

      {/* Image Gallery Section */}
      {isPremium && (
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-white flex items-center gap-2">
              <Image size={18} className="text-indigo-400" />
              Image Gallery
            </h4>
            <span className="text-sm font-medium text-indigo-300">{galleryCount} saved</span>
          </div>

          <p className="text-xs text-gray-400 mb-3">
            Every AI-generated image is automatically saved here. Browse and reuse them without spending tokens.
          </p>

          {galleryCount > 0 ? (
            <button
              onClick={() => setShowGallery(true)}
              className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Image size={14} />
              Open Gallery ({galleryCount} images)
            </button>
          ) : (
            <div className="p-3 bg-gray-800/50 rounded border border-gray-700 text-center">
              <p className="text-xs text-gray-500">
                No images yet. Generate a portrait or item icon to start building your gallery.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Gallery modal */}
      {showGallery && (
        <Suspense fallback={null}>
          <ImageGallery
            images={allGalleryImages}
            grouped={true}
            initialFilter="all"
            onSelectImage={null}
            onDeleteImage={deleteFromGallery}
            onClose={() => setShowGallery(false)}
          />
        </Suspense>
      )}

      {/* Billing Section */}
      {isPremium && (
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 p-5">
          <h4 className="font-bold text-white flex items-center gap-2 mb-3">
            <CreditCard size={18} className="text-green-400" />
            Billing
          </h4>
          <p className="text-xs text-gray-400">
            Your one-time purchase covers all platforms (web, Android, iOS).
            Token packs are processed through Stripe.
          </p>
        </div>
      )}
    </div>
  );
};

export default AccountPanel;
